import { useState, type ReactNode } from "react";

// The coral lock screen (canvas "gate") every device app shows before sign-in, and instead of
// the app when it can't run (not enabled, unpaired, plan ended). Styles: @cliqsuite/ui/coral.css.

export const Icon = ({ name, size = 20, color }: { name: string; size?: number; color?: string }) => (
  <span className="ms" aria-hidden="true" style={{ fontSize: size, color }}>
    {name}
  </span>
);

/**
 * The screen an app shows while it starts up (canvas "… · Starting up"): the wordmark, what it is
 * doing, and a running bar. `dark` is Admin's ink background.
 */
export function StartupScreen({ app, hint = "Just a moment", label, dark }: { app?: string; hint?: string; label: string; dark?: boolean }) {
  return (
    <div className={`gate gate-start${dark ? " dark" : ""}`}>
      <div className="gate-word" aria-hidden="true">
        CLIQ
        <br />
        SUITE
        {app && (
          <>
            <br />
            <span>{app}</span>
          </>
        )}
      </div>
      {/* where the sign-in card or the app lands, so the screen doesn't jump when it does */}
      <div className="gate-ghost" aria-hidden="true">
        <i />
        <b style={{ width: "70%" }} />
        <b style={{ width: "90%" }} />
        <b style={{ width: "55%" }} />
        <em />
      </div>
      <div className="gate-side" role="status">
        <small>{hint}</small>
        <b>{label}</b>
        <div className="gate-run" aria-hidden="true">
          <i />
        </div>
      </div>
    </div>
  );
}

/** `app` is the dark third word of the big wordmark: "POS", "KDS". */
export function Gate({ app, side, children, wide }: { app: string; side: ReactNode; children: ReactNode; wide?: boolean }) {
  // Phones: while the code is being typed the card stretches up to the top of the screen (coral.css,
  // .gate.typing), so the boxes and the button sit above the keyboard on any phone and the card still
  // reaches the bottom edge behind it. It follows the input's focus, not the keyboard's measured
  // height: that arrives late and differs per browser, which made the card jump. A touch on the boxes
  // counts already, so the card is on its way before the keyboard is.
  const [typing, setTyping] = useState(false);
  const isCode = (el: EventTarget) => el instanceof Element && !!el.closest(".gate-code");
  return (
    <div
      className={`gate cq-full-height${typing ? " typing" : ""}`}
      onPointerDown={(e) => isCode(e.target) && setTyping(true)}
      onFocus={(e) => isCode(e.target) && setTyping(true)}
      onBlur={(e) => isCode(e.target) && setTyping(false)}
    >
      <div className="gate-word" aria-hidden="true">
        CLIQ
        <br />
        SUITE
        <br />
        <span>{app}</span>
      </div>
      <div className="gate-side">{side}</div>
      <main className="gate-card" style={wide ? { width: "min(560px, calc(100% - 32px))" } : undefined}>
        {children}
      </main>
      {/* Phones: Safari colours the band behind its address bar from a fixed element at the bottom
          edge, else from the page (coral). This keeps the band white, as if the card ran on under it. */}
      <div className="gate-foot" aria-hidden="true" />
    </div>
  );
}

export const GateBadge = ({ icon, bg, fg }: { icon: string; bg: string; fg: string }) => (
  <div className="gate-badge" style={{ background: bg }}>
    <Icon name={icon} size={30} color={fg} />
  </div>
);

/** Canvas code boxes: four letters, a dash, four digits. Typing goes to one hidden input.
 * Returns the typed code and the boxes to render. */
export function usePairingCode({ error, onSubmit, onChange }: { error: boolean; onSubmit: (code: string) => void; onChange?: () => void }) {
  const [code, setCode] = useState("");
  const chars = code.split("");
  return {
    code,
    boxes: (
      <div className={`gate-code${error ? " err" : ""}`}>
        {Array.from({ length: 8 }, (_, i) => (
          <span key={i} className={`${chars[i] ? "on" : ""}${i === chars.length ? " cur" : ""}`}>
            {chars[i] ?? ""}
          </span>
        )).flatMap((box, i) => (i === 4 ? [<i key="dash" />, box] : [box]))}
        <input
          aria-label="Pairing code"
          // A till with a keyboard types straight away; a phone waits for a tap, so its card starts at rest.
          autoFocus={window.matchMedia("(min-width: 880px)").matches}
          autoComplete="off"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8));
            onChange?.();
          }}
          onKeyDown={(e) => e.key === "Enter" && onSubmit(code)}
        />
      </div>
    ),
  };
}

export const GateError = ({ children }: { children: ReactNode }) => (
  <div role="alert" className="gate-error">
    <Icon name="error" size={18} />
    {children}
  </div>
);
